import type { Workflow, StageData, WorkflowStage } from '@/components/ApprovalStageWizard/types';
import type { ApprovalWorkflow as BackendApprovalWorkflow } from '@/types/workflow';
import type { ApprovalWorkflow } from '../types';

// Helper function to convert mock approvals to workflows
export const convertMockApprovalsToWorkflows = (approvals: ApprovalWorkflow[]): Workflow[] => {
  return approvals.map((approval) => {
    const stages: WorkflowStage[] = [];
    for (let i = 1; i <= approval.stages; i++) {
      stages.push({
        id: `${approval.id}-stage-${i}`,
        order: i,
        name: `Stage ${i}`,
        description: `Approval stage ${i}`,
        status: approval.status === 'Active' ? 'configured' : 'draft',
      });
    }
    return {
      id: approval.id,
      name: approval.name,
      status: approval.status.toLowerCase() as 'draft' | 'active' | 'inactive',
      stages,
      createdAt: approval.lastModified,
      updatedAt: approval.lastModified,
      createdBy: 'system',
      assignedToCount: approval.assignedEmployees,
    };
  });
};

// Helper function to map backend approver type to frontend approver type
export const mapBackendApproverTypeToFrontend = (backendType: string): 'role' | 'user' | 'rule' => {
  switch (backendType) {
    case 'specific':
      return 'user';
    case 'role':
    case 'manager':
    case 'department_head':
      return 'role';
    case 'custom':
      return 'rule';
    default:
      return 'role';
  }
};

// Helper function to map backend approver role to frontend role type
export const mapBackendRoleToFrontend = (backendType: string, backendRole?: string): 'DirectManager' | 'ProjectManager' | 'DepartmentHead' | undefined => {
  if (backendType === 'manager') return 'DirectManager';
  if (backendType === 'department_head') return 'DepartmentHead';
  if (backendRole) {
    switch (backendRole.toLowerCase()) {
      case 'manager':
        return 'DirectManager';
      case 'project_manager':
        return 'ProjectManager';
      case 'department_head':
        return 'DepartmentHead';
      default:
        return 'DirectManager';
    }
  }
  return undefined;
};

// Helper function to convert backend workflow to frontend Workflow type
export const mapBackendWorkflowToFrontend = (backendWorkflow: BackendApprovalWorkflow): Workflow => {
  return {
    id: backendWorkflow.id,
    name: backendWorkflow.name,
    description: backendWorkflow.description,
    status: backendWorkflow.isActive ? 'active' : 'inactive',
    stages: backendWorkflow.stages.map((stage, index) => {
      // Get the first approver from the stage (if any)
      const firstApprover = stage.approvers?.[0];

      // Parse advanced config JSON if present
      let advancedConfig: {
        smartRules?: {
          rules?: unknown[];
          onApproved?: { type: string; targetStageId?: string; targetStageName?: string };
          onRejected?: { type: string; targetStageId?: string; targetStageName?: string };
          timeout?: { enabled: boolean; days: number; action: string; escalateTo?: string; escalateToName?: string };
        };
        notifications?: {
          onApproved?: unknown[];
          onRejected?: unknown[];
        };
      } | undefined;

      if (stage.advancedConfigJson) {
        try {
          advancedConfig = JSON.parse(stage.advancedConfigJson);
        } catch (e) {
          console.warn('Failed to parse advancedConfigJson for stage:', stage.id, e);
        }
      }

      // Parse rule config JSON for approver if present
      let ruleDetermined: unknown | undefined;
      if (firstApprover?.ruleConfigJson) {
        try {
          ruleDetermined = JSON.parse(firstApprover.ruleConfigJson);
        } catch (e) {
          console.warn('Failed to parse ruleConfigJson for approver:', firstApprover.id, e);
        }
      }

      // Build stageData from backend approver info and advanced config
      // IMPORTANT: Always populate stageData to preserve rules when updating other stages
      const stageData: StageData = {
        basicDetails: {
          stageName: stage.name,
          description: stage.description,
        },
        approver: firstApprover ? {
          approverType: mapBackendApproverTypeToFrontend(firstApprover.approverType),
          roleType: mapBackendRoleToFrontend(firstApprover.approverType, firstApprover.approverRole),
          specificUserId: firstApprover.approverId != null ? String(firstApprover.approverId) : undefined,
          specificUserName: firstApprover.approverName,
          cannotApproveOwn: firstApprover.cannotApproveOwn ?? true,
          ruleDetermined: ruleDetermined as StageData['approver']['ruleDetermined'],
        } : {
          // Default approver when none exists
          approverType: 'role',
          roleType: 'DirectManager',
          cannotApproveOwn: true,
        },
        smartRules: {
          rules: (advancedConfig?.smartRules?.rules ?? []) as StageData['smartRules']['rules'],
          onApproved: (advancedConfig?.smartRules?.onApproved ?? { type: 'continue' }) as StageData['smartRules']['onApproved'],
          onRejected: (advancedConfig?.smartRules?.onRejected ?? { type: 'reject' }) as StageData['smartRules']['onRejected'],
          timeout: advancedConfig?.smartRules?.timeout as StageData['smartRules']['timeout'],
        },
        notifications: {
          onApproved: (advancedConfig?.notifications?.onApproved ?? []) as StageData['notifications']['onApproved'],
          onRejected: (advancedConfig?.notifications?.onRejected ?? []) as StageData['notifications']['onRejected'],
        },
        allowApproverEdit: stage.allowApproverEdit,
      };

      return {
        id: stage.id || `${backendWorkflow.id}-stage-${index + 1}`,
        order: stage.stageOrder,
        name: stage.name,
        description: stage.description,
        status: backendWorkflow.isActive ? 'configured' : 'draft',
        stageData,
      };
    }),
    createdAt: new Date(backendWorkflow.createdAt),
    updatedAt: backendWorkflow.updatedAt ? new Date(backendWorkflow.updatedAt) : new Date(backendWorkflow.createdAt),
    createdBy: backendWorkflow.createdBy,
    assignedToCount: 0, // Backend doesn't return this
  };
};

// Helper function to convert backend workflow to ApprovalWorkflow for list display
export const mapBackendWorkflowToApproval = (backendWorkflow: BackendApprovalWorkflow): ApprovalWorkflow => {
  return {
    id: backendWorkflow.id,
    name: backendWorkflow.name,
    status: backendWorkflow.isActive ? 'Active' : 'Inactive',
    stages: backendWorkflow.stages.length,
    assignedEmployees: 0, // Backend doesn't return this
    lastModified: backendWorkflow.updatedAt ? new Date(backendWorkflow.updatedAt) : new Date(backendWorkflow.createdAt),
    isDefault: backendWorkflow.isDefault,
  };
};

// Helper function to map frontend approver type to backend approver type
export const mapApproverTypeToBackend = (frontendType: string): 'specific' | 'role' | 'manager' | 'department_head' | 'custom' => {
  switch (frontendType) {
    case 'user':
      return 'specific';
    case 'role':
      return 'role';
    case 'rule':
      return 'custom';
    case 'manager':
      return 'manager';
    case 'department_head':
      return 'department_head';
    default:
      return 'manager'; // Default to manager if unknown type
  }
};

// Helper function to map frontend role type to backend approver role
export const mapRoleTypeToBackend = (roleType?: string): string | undefined => {
  if (!roleType) return undefined;
  switch (roleType) {
    case 'DirectManager':
      return 'manager';
    case 'ProjectManager':
      return 'project_manager';
    case 'DepartmentHead':
      return 'department_head';
    default:
      return roleType.toLowerCase();
  }
};
